'use client';

import { useEffect, useRef } from 'react';
import { useToast } from '@/lib/context/ToastContext';

interface StorageQuotaWarningEvent extends CustomEvent {
  detail: {
    level: 'warning' | 'critical';
    percent: number;
    usageMB: string;
    quotaMB: string;
  };
}

// 5 minutes - don't spam warnings
const DEBOUNCE_INTERVAL = 5 * 60 * 1000;

/**
 * Hook to monitor storage quota and display warnings to users
 *
 * Listens for 'storage-quota-warning' events from StorageMonitor
 * and displays toast notifications when storage is running low.
 *
 * Works in both development and production.
 */
export function useStorageQuotaMonitor() {
  const { warning, error } = useToast();
  const lastWarningTimeRef = useRef<number>(0);

  useEffect(() => {
    const handleQuotaWarning = (event: Event) => {
      const { detail } = event as StorageQuotaWarningEvent;
      const now = Date.now();

      // Debounce: Don't show the same warning more than once per 5 minutes
      if (now - lastWarningTimeRef.current < DEBOUNCE_INTERVAL) {
        return;
      }

      lastWarningTimeRef.current = now;

      if (detail.level === 'critical') {
        error(
          `Storage space critically low! Using ${detail.usageMB}MB of ${detail.quotaMB}MB (${detail.percent.toFixed(1)}%). Some features may not work correctly. Please clear your browser data.`,
          { duration: 10000 } // 10 seconds
        );
      } else {
        warning(
          `Storage space running low. Using ${detail.usageMB}MB of ${detail.quotaMB}MB (${detail.percent.toFixed(1)}%). Consider clearing old data.`,
          { duration: 8000 } // 8 seconds
        );
      }
    };

    window.addEventListener('storage-quota-warning', handleQuotaWarning);

    return () => {
      window.removeEventListener('storage-quota-warning', handleQuotaWarning);
    };
  }, [warning, error]);
}
